export type LibraryCatalogKind = 'product' | 'wine' | 'recipe';
export type LibrarySort = 'article' | 'alpha' | 'newest';

export interface LibraryCategory {
  _id: string;
  name: string;
  parentCategory?: string | { _id: string; name: string } | null;
}

function parentId(category: LibraryCategory): string | null {
  if (!category.parentCategory) return null;
  return typeof category.parentCategory === 'string' ? category.parentCategory : category.parentCategory._id;
}

function categoryName(category: LibraryCategory): string {
  return String(category.name || '').trim().toLowerCase();
}

function findRoot(categories: LibraryCategory[], name: string): LibraryCategory | undefined {
  return categories.find((category) => categoryName(category) === name && !parentId(category))
    || categories.find((category) => categoryName(category) === name);
}

export function normalizeCategoryId(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'object' && value && '_id' in value) return String((value as { _id: unknown })._id);
  return String(value);
}

export function resolveCatalogKind(
  selectedId: string | null | undefined,
  categories: LibraryCategory[],
): LibraryCatalogKind | null {
  if (!selectedId) return null;
  const productsRoot = findRoot(categories, 'produkter');
  const wineRoot = findRoot(categories, 'vin');
  const recipeRoot = findRoot(categories, 'recept');
  const byId = new Map(categories.map((category) => [category._id, category]));
  let current: string | null = selectedId;
  const visited = new Set<string>();
  while (current && !visited.has(current)) {
    if (productsRoot && current === productsRoot._id) return 'product';
    if (wineRoot && current === wineRoot._id) return 'wine';
    if (recipeRoot && current === recipeRoot._id) return 'recipe';
    visited.add(current);
    const category = byId.get(current);
    current = category ? parentId(category) : null;
  }
  return null;
}

export function defaultLibrarySort(catalog: LibraryCatalogKind | null): LibrarySort {
  return catalog === 'product' || catalog === 'wine' ? 'article' : 'newest';
}

export function isLibrarySort(value: unknown): value is LibrarySort {
  return value === 'article' || value === 'alpha' || value === 'newest';
}

export function expandCategoryAncestors(categoryIds: string[], categories: LibraryCategory[]): string[] {
  const byId = new Map(categories.map((category) => [category._id, category]));
  const expanded = new Set<string>();
  for (const id of categoryIds.map(normalizeCategoryId).filter(Boolean)) {
    let current: string | null = id;
    const visited = new Set<string>();
    while (current && !visited.has(current)) {
      expanded.add(current);
      visited.add(current);
      const category = byId.get(current);
      current = category ? parentId(category) : null;
    }
  }
  return Array.from(expanded);
}

export function catalogKindForAsset(
  assetCategoryIds: string[],
  categories: LibraryCategory[],
): LibraryCatalogKind | null {
  for (const id of expandCategoryAncestors(assetCategoryIds, categories)) {
    const kind = resolveCatalogKind(id, categories);
    if (kind) return kind;
  }
  return null;
}

export function matchLibraryTemplate<T extends {
  categoryIds?: unknown[];
  fullPageLayoutProfile?: string;
  name?: string;
}>(
  assetCategoryIds: string[],
  templates: T[],
  categories: LibraryCategory[],
): T | null {
  const expanded = new Set(expandCategoryAncestors(assetCategoryIds, categories));
  if (!expanded.size) return null;
  const hits = templates.filter((template) =>
    (template.categoryIds || []).some((id) => expanded.has(normalizeCategoryId(id))),
  );
  if (!hits.length) return null;
  const kind = catalogKindForAsset(assetCategoryIds, categories);
  const profile = kind === 'product' || kind === 'wine' || kind === 'recipe' ? kind : null;
  if (profile) {
    const byProfile = hits.find((template) => template.fullPageLayoutProfile === profile);
    if (byProfile) return byProfile;
  }
  if (kind === 'product') return hits.find((template) => /produkt/i.test(template.name || '')) || hits[0];
  if (kind === 'wine') return hits.find((template) => /^vin$/i.test(String(template.name || '').trim())) || hits[0];
  if (kind === 'recipe') return hits.find((template) => /recept/i.test(template.name || '')) || hits[0];
  return hits[0];
}
